import React, { useEffect, useState } from 'react';
import { HiInformationCircle } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import { Alert, Button, Spinner } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  CreateQueueDelegation,
  GetQueueDelegations,
  PortalRoleMemberHit,
  QueueDelegation,
  QueueDelegationsResponse,
  RevokeQueueDelegation,
  SearchDelegationTargets,
} from '../../services/dtos';
import { fmtDate, fmtDateTime, roleLabel } from './supportShared';

// T5: out-of-office coverage. A role holder hands their lane (queue or
// escalation target) to a covering person for a date range; while it's
// active the delegate can work that lane's tickets and receives its
// notifications. The server enforces who may delegate what — the form's
// role picker only offers lanes the caller holds (all of them for admins).
const SupportDelegationsPage: React.FC = () => {
  const [data, setData] = useState<QueueDelegationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // form state
  const [role, setRole] = useState('');
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<PortalRoleMemberHit[]>([]);
  const [target, setTarget] = useState<PortalRoleMemberHit | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    apiServices.client
      .get(new GetQueueDelegations())
      .then((res: QueueDelegationsResponse) => {
        setData(res);
        setLoading(false);
      })
      .catch((e: any) => {
        setError(e?.responseStatus?.message || 'Failed to load delegations');
        setLoading(false);
      });
  }, []);

  // covering-person search, debounced
  useEffect(() => {
    if (target || query.trim().length < 2) {
      setHits([]);
      return;
    }
    const timer = setTimeout(() => {
      const req = new SearchDelegationTargets();
      req.q = query.trim();
      apiServices.client
        .get(req)
        .then(setHits)
        .catch(() => setHits([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [query, target]);

  const create = async () => {
    if (busy || !role || !target || !startDate || !endDate) return;
    setBusy(true);
    setError(null);
    try {
      const req = new CreateQueueDelegation();
      req.queueRole = role;
      req.delegateUniqueMemberId = target.uniqueMemberId;
      req.startDate = startDate as any;
      req.endDate = endDate as any;
      setData(await apiServices.client.post(req));
      setRole('');
      setQuery('');
      setTarget(null);
      setStartDate('');
      setEndDate('');
    } catch (e: any) {
      setError(e?.responseStatus?.message || 'Failed to create the delegation');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (d: QueueDelegation) => {
    if (busy) return;
    if (!window.confirm(`End the ${roleLabel(d.queueRole)} delegation to ${d.delegateName}?`))
      return;
    setBusy(true);
    setError(null);
    try {
      const req = new RevokeQueueDelegation();
      req.id = d.id;
      setData(await apiServices.client.post(req));
    } catch (e: any) {
      setError(e?.responseStatus?.message || 'Failed to revoke the delegation');
    } finally {
      setBusy(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center pt-10">
        <Spinner color="info" />
      </div>
    );

  const delegable = data?.delegableRoles || [];
  const current = (data?.delegations || []).filter((d) => !d.revokedAtUtc);
  const past = (data?.delegations || []).filter((d) => d.revokedAtUtc);

  const inputCls =
    'block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white';

  return (
    <div className="mx-auto w-full flex max-w-4xl flex-col gap-6 dark:text-white">
      <div>
        <Link to="/help/queue" className="text-sm text-indigo-600 dark:text-indigo-400">
          ← Back to the queue
        </Link>
        <h2 className="mt-1 text-2xl font-bold">Out of Office — Queue Delegations</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Hand your queue to a covering person for a date range. While it's
          active they can see and work your lane's tickets — confidential ones
          included — and they get its notifications. Every delegation is
          recorded.
        </p>
      </div>

      {error && (
        <Alert color="failure" icon={HiInformationCircle}>
          {error}
        </Alert>
      )}

      {delegable.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Set up coverage
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                Queue / role to hand over
              </label>
              <select
                className={inputCls}
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="">Choose…</option>
                {delegable.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative">
              <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                Covering person
              </label>
              <input
                className={inputCls}
                placeholder="Search by name or email…"
                value={target ? `${target.name} (${target.uniqueMemberId})` : query}
                onChange={(e) => {
                  setTarget(null);
                  setQuery(e.target.value);
                }}
              />
              {hits.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow">
                  {hits.map((h) => (
                    <li key={h.uniqueMemberId}>
                      <button
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => {
                          setTarget(h);
                          setHits([]);
                        }}
                      >
                        <span className="font-medium">{h.name}</span>{' '}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {h.email} · {h.membershipStatus}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                First day
              </label>
              <input
                type="date"
                className={inputCls}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                Last day (inclusive)
              </label>
              <input
                type="date"
                className={inputCls}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Button
              onClick={create}
              disabled={busy || !role || !target || !startDate || !endDate}
            >
              {busy ? <Spinner size="sm" /> : 'Delegate'}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Current & upcoming
        </h3>
        {current.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No active delegations.
          </div>
        )}
        {current.map((d) => (
          <div
            key={d.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-sm"
          >
            <div className="flex flex-col">
              <span className="font-medium text-gray-900 dark:text-white">
                {roleLabel(d.queueRole)} → {d.delegateName}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {fmtDate(d.startDate)} – {fmtDate(d.endDate)} · set up by{' '}
                {d.createdByName}
              </span>
            </div>
            <Button size="xs" color="gray" onClick={() => revoke(d)} disabled={busy}>
              End now
            </Button>
          </div>
        ))}
      </div>

      {past.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            History
          </h3>
          {past.map((d) => (
            <div
              key={d.id}
              className="rounded-lg border border-gray-100 dark:border-gray-800 p-3 text-xs text-gray-500 dark:text-gray-400"
            >
              {roleLabel(d.queueRole)} → {d.delegateName} · {fmtDate(d.startDate)} –{' '}
              {fmtDate(d.endDate)} · ended {fmtDateTime(d.revokedAtUtc)} by{' '}
              {d.revokedByName}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupportDelegationsPage;
